/**
 * Telegram WebApp SDK ustidan yupqa qatlam.
 *
 * DIQQAT: `initDataUnsafe` nomiga mos — undan FAQAT ko'rinish uchun
 * foydalanamiz. Autentifikatsiya har doim xom `initData` satri orqali,
 * uni server HMAC bilan tekshiradi (TZ §13.2).
 *
 * Telegramdan tashqarida (brauzerda `npm run dev`) SDK yo'q. Shunda
 * VITE_DEV_INIT_DATA ishlatiladi — bu faqat ishlab chiqish uchun.
 */

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  section_bg_color?: string;
  section_separator_color?: string;
  destructive_text_color?: string;
}

interface BottomButton {
  text: string;
  isVisible: boolean;
  show(): void;
  hide(): void;
  setText(text: string): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  enable(): void;
  disable(): void;
  /** `false` — Telegram mavzusidagi standart rangga qaytarish. */
  setParams?(params: {
    color?: string | false;
    text_color?: string | false;
    has_shine_effect?: boolean;
  }): void;
}

interface BackButton {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

interface HapticFeedback {
  impactOccurred(style: "light" | "medium" | "heavy"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; first_name?: string; language_code?: string };
    start_param?: string;
  };
  version: string;
  colorScheme: "light" | "dark";
  themeParams: ThemeParams;
  isExpanded: boolean;
  viewportStableHeight: number;
  MainButton: BottomButton;
  BackButton: BackButton;
  HapticFeedback: HapticFeedback;
  ready(): void;
  expand(): void;
  close(): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
  showAlert(message: string, cb?: () => void): void;
  setHeaderColor(color: string): void;
  setBackgroundColor?(color: string): void;
  disableVerticalSwipes?(): void;
  isVersionAtLeast?(version: string): boolean;
  onEvent?(event: "themeChanged", cb: () => void): void;
  /** Bot API 6.9+: foydalanuvchiga bog'liq, qurilmalar orasida umumiy. */
  CloudStorage?: {
    setItem(key: string, value: string, cb?: (err: unknown) => void): void;
    getItem(key: string, cb: (err: unknown, value?: string) => void): void;
    removeItem(key: string, cb?: (err: unknown) => void): void;
  };
}

/**
 * Brend foni (styles.css dagi --navy-deep bilan bir xil).
 * Ilova o'z quyuq foniga ega, shuning uchun Telegram chrome'i ham
 * shunga bo'yaladi — aks holda sarlavha oq bo'lib, ilova ustida
 * yorug' chiziq qolib ketadi.
 */
const BRAND_BG = "#091a33";

/**
 * Kunduzgi mavzu foni — styles.css dagi `[data-theme="light"]`
 * gradientining YUQORI rangi. Sarlavha shu rangda bo'lsa, Telegram
 * chrome'i bilan ilova orasida chok ko'rinmaydi.
 */
const LIGHT_HEADER = "#e7effb";
const LIGHT_BG = "#eef3fa";

/**
 * Kunduzgi mavzuda ariza tugmasi (MainButton) brend oltinida — u
 * ilovadagi `.btn-primary` bilan bir xil ma'noni tashiydi. Matn
 * oltin ustida 9.3:1.
 */
const GOLD = "#f5b921";
const ON_GOLD = "#2a1d02";

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const webApp: TelegramWebApp | undefined =
  typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;

export const insideTelegram = Boolean(webApp?.initData);

/**
 * Mijoz shu Bot API versiyasini qo'llab-quvvatlaydimi.
 *
 * NEGA KERAK: qo'llab-quvvatlanmagan usulni chaqirsak SDK xato
 * TASHLAMAYDI — jimgina konsolga ogohlantirish yozadi va hech narsa
 * qilmaydi. Ya'ni try/catch yordam bermaydi, oldindan tekshirish kerak.
 *
 * `isVersionAtLeast` ning o'zi ham 6.1 da paydo bo'lgan; yo'q bo'lsa
 * mijoz eski deb hisoblanadi.
 */
export function supports(version: string): boolean {
  return Boolean(webApp?.isVersionAtLeast?.(version));
}

/**
 * Server uchun autentifikatsiya satri.
 *
 * `import.meta.env.DEV` SHART: Vite `import.meta.env.VITE_*` havolalarini
 * build vaqtida SATRGA almashtiradi — runtime tekshiruvi emas. Guard
 * bo'lmasa, ishlab chiqarish build'ida ham haqiqiy imzolangan initData
 * (masalan admin hisobiga) JS bundle ichida ochiq matn sifatida qoladi
 * va uni bundle'ni yuklab olgan HAR KIM o'qiy oladi. `DEV` production'da
 * statik `false` bo'lgani uchun minifikator butun blokni butunlay olib
 * tashlaydi — qiymat bundle'ga umuman tushmaydi.
 */
export function getInitData(): string {
  if (webApp?.initData) return webApp.initData;
  if (import.meta.env.DEV) {
    const dev = import.meta.env.VITE_DEV_INIT_DATA;
    return typeof dev === "string" ? dev : "";
  }
  return "";
}

/** Telegram mavzusini CSS o'zgaruvchilariga ko'chiradi. */
function applyTheme(): void {
  if (!webApp) return;
  const root = document.documentElement;
  const params = webApp.themeParams;
  const map: Record<string, string | undefined> = {
    "--tg-bg": params.bg_color,
    "--tg-text": params.text_color,
    "--tg-hint": params.hint_color,
    "--tg-link": params.link_color,
    "--tg-button": params.button_color,
    "--tg-button-text": params.button_text_color,
    "--tg-secondary-bg": params.secondary_bg_color,
    "--tg-section-bg": params.section_bg_color,
    "--tg-separator": params.section_separator_color,
    "--tg-destructive": params.destructive_text_color,
  };
  for (const [name, value] of Object.entries(map)) {
    if (value) root.style.setProperty(name, value);
  }
}

/**
 * Foydalanuvchi ILOVADA tanlagan mavzu. `auto` — hech narsa tanlanmagan,
 * mavzu Telegram'dan keladi.
 *
 * IKKI JOYDA saqlanadi:
 *   * localStorage — sinxron, birinchi kadrdan oldin o'qiladi
 *     (`public/theme-boot.js`), ya'ni ekran boshqa mavzuda "miltillamaydi";
 *   * Telegram CloudStorage — foydalanuvchiga bog'liq: WebView xotirasi
 *     tozalansa yoki boshqa qurilmadan kirilsa ham tanlov qaytadi.
 *     U asinxron, shuning uchun faqat localStorage bo'sh bo'lganda
 *     o'qiladi (`initTelegram`).
 *
 * Kalit nomlari har xil: CloudStorage nuqtani qabul qilmaydi
 * (faqat A-Z, a-z, 0-9, `_`, `-`).
 */
export type ThemePref = "auto" | "light" | "dark";
const THEME_KEY = "gs.theme";
const CLOUD_THEME_KEY = "gs_theme";

const isScheme = (v: unknown): v is "light" | "dark" => v === "light" || v === "dark";

export function themePref(): ThemePref {
  try {
    const value = localStorage.getItem(THEME_KEY);
    return isScheme(value) ? value : "auto";
  } catch {
    return "auto";          // bloklangan saqlash, maxfiy oyna
  }
}

/** CloudStorage 6.9 da qo'shilgan; eski mijozda tanlov faqat shu qurilmada. */
function cloud() {
  return insideTelegram && supports("6.9") ? webApp?.CloudStorage : undefined;
}

/**
 * Kunduz yoki tun. Avval ILOVADAGI tanlov; tanlanmagan bo'lsa Telegram
 * ichida — Telegram mavzusi (u odatda "avtomatik tungi rejim" bilan kun
 * vaqtiga qarab almashadi), tashqarida — tizim sozlamasi. Birinchi
 * kadrdagi qiymatni `public/theme-boot.js` qo'yadi; bu yer bilan bir xil
 * qoida, aks holda ekran bir zum boshqa mavzuda chizilib, keyin sakrardi.
 */
export function colorScheme(): "light" | "dark" {
  const pref = themePref();
  if (pref !== "auto") return pref;
  if (insideTelegram && webApp) return webApp.colorScheme;
  return window.matchMedia?.("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

/** Mavzu almashganini kutayotganlar (mavzu tugmasi ikonkasi). */
const schemeListeners = new Set<() => void>();

export function onSchemeChange(cb: () => void): () => void {
  schemeListeners.add(cb);
  return () => {
    schemeListeners.delete(cb);
  };
}

/**
 * Foydalanuvchi ilovada mavzu tanladi. Keyingi kirishlarda ham shu
 * qoladi — Telegram mavzusi o'zgarsa ham. `auto` tanlovni o'chiradi va
 * ilova yana Telegram'ga ergashadi.
 *
 * Almashish silliq: brauzer View Transitions'ni bilsa eski va yangi
 * ko'rinish bir-biriga o'tadi (keskin "chaqnash" yo'q). Bilmasa yoki
 * harakat kamaytirilgan bo'lsa — darhol.
 */
export function setThemePref(pref: ThemePref): void {
  try {
    if (pref === "auto") localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, pref);
  } catch {
    /* saqlab bo'lmasa — shu seans uchun baribir almashadi (pastda) */
  }
  try {
    const store = cloud();
    if (pref === "auto") store?.removeItem(CLOUD_THEME_KEY);
    else store?.setItem(CLOUD_THEME_KEY, pref);
  } catch {
    /* bulutga yozilmasa — tanlov shu qurilmada qoladi */
  }
  const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
  const calm = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  if (doc.startViewTransition && !calm) doc.startViewTransition(applyColorScheme);
  else applyColorScheme();
}

let mainButtonPainted = false;

/**
 * Mavzuni ildizga qo'yadi va Telegram chrome'ini unga moslaydi.
 * Tungi mavzu ilgarigidek: navy sarlavha, MainButton Telegram rangida.
 */
function applyColorScheme(): void {
  const scheme = colorScheme();
  document.documentElement.dataset.theme = scheme;
  for (const cb of schemeListeners) cb();
  if (!insideTelegram || !webApp) return;

  // 6.1 dan oldin bu usullar yo'q — eski mijoz Telegram mavzusida
  // qoladi, ilova baribir ishlaydi.
  if (supports("6.1")) {
    webApp.setHeaderColor(scheme === "light" ? LIGHT_HEADER : BRAND_BG);
    webApp.setBackgroundColor?.(scheme === "light" ? LIGHT_BG : BRAND_BG);
  }

  // Parametrlar tugma yashirilganda ham saqlanadi — bir marta bo'yash
  // kifoya. Tunda faqat ilgari bo'yalgan bo'lsa standartga qaytariladi,
  // ya'ni tungi foydalanuvchi uchun hech narsa o'zgarmaydi.
  const button = webApp.MainButton;
  try {
    if (scheme === "light") {
      button.setParams?.({ color: GOLD, text_color: ON_GOLD, has_shine_effect: true });
      mainButtonPainted = true;
    } else if (mainButtonPainted) {
      button.setParams?.({ color: false, text_color: false, has_shine_effect: false });
      mainButtonPainted = false;
    }
  } catch {
    // Eski SDK rangni rad etsa — tugma Telegram rangida qoladi
  }
}

export function initTelegram(): void {
  // Brauzerda ham kerak: tizim mavzusi almashsa ilova ham almashadi
  window
    .matchMedia?.("(prefers-color-scheme: light)")
    .addEventListener?.("change", applyColorScheme);
  if (!webApp) {
    applyColorScheme();
    return;
  }
  webApp.ready();
  webApp.expand();
  applyTheme();
  applyColorScheme();
  webApp.onEvent?.("themeChanged", () => {
    applyTheme();
    applyColorScheme();
  });

  // Bu qurilmada tanlov yo'q — boshqa qurilmada yoki xotira tozalanishidan
  // oldin tanlangan bo'lishi mumkin. Topilsa, shu yerga ham yoziladi:
  // keyingi safar birinchi kadrdanoq to'g'ri mavzu.
  const store = cloud();
  if (store && themePref() === "auto") {
    try {
      store.getItem(CLOUD_THEME_KEY, (err, value) => {
        if (err || !isScheme(value) || themePref() !== "auto") return;
        try {
          localStorage.setItem(THEME_KEY, value);
        } catch {
          /* faqat shu seans uchun */
        }
        applyColorScheme();
      });
    } catch {
      /* bulut javob bermasa — Telegram mavzusi qoladi */
    }
  }

  // Vertikal svayp Mini App'ni tasodifan yopib yubormasin (7.7+)
  if (supports("7.7")) webApp.disableVerticalSwipes?.();
}

/** Tebranish javobi. 6.1 dan oldin yo'q — jimgina o'tkazib yuboriladi. */
export function haptic(kind: "light" | "medium" | "success" | "error"): void {
  if (!supports("6.1")) return;
  const h = webApp?.HapticFeedback;
  if (!h) return;
  if (kind === "success" || kind === "error") h.notificationOccurred(kind);
  else h.impactOccurred(kind);
}

/** Tashqi havolani Telegram brauzerida ochadi. */
export function openExternal(url: string): void {
  if (webApp) webApp.openLink(url);
  else window.open(url, "_blank", "noopener,noreferrer");
}

/** Telegram ichidagi havola (ulashish oynasi). */
export function openTelegram(url: string): void {
  if (webApp) webApp.openTelegramLink(url);
  else window.open(url, "_blank", "noopener,noreferrer");
}

/** Xabar oynasi. `showAlert` 6.2 da qo'shilgan — eskisida brauzerniki. */
export function alertUser(message: string): void {
  if (webApp && supports("6.2")) webApp.showAlert(message);
  else window.alert(message);
}
